import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { MusicNote, Edit, Close } from '@mui/icons-material';

function StartNewModal({ isOpen, onClose, onStartFromTheme, onStartFromLyric }) {
  const [projectName, setProjectName] = useState('');
  const [theme, setTheme] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [style, setStyle] = useState('cartoon');
  const [imageFormat, setImageFormat] = useState('16:9');
  const [creationMode, setCreationMode] = useState(null); // null, 'theme', or 'lyrics'

  const handleStartFromTheme = () => {
    if (!projectName.trim()) return;
    onStartFromTheme(projectName.trim(), theme.trim() || "", style, imageFormat);
    setProjectName('');
    setTheme('');
    setStyle('realistic');
    setImageFormat('16:9');
    setCreationMode(null);
  };

  const handleStartFromLyric = () => {
    if (!projectName.trim() || !theme.trim() || !lyrics.trim()) return;
    onStartFromLyric(projectName.trim(), theme.trim(), lyrics.trim(), style, imageFormat);
    setProjectName('');
    setTheme('');
    setLyrics('');
    setStyle('cartoon');
    setImageFormat('16:9');
    setCreationMode(null);
  };

  const handleModeSelection = (mode) => {
    setCreationMode(mode);
  };

  const handleBackToModeSelection = () => {
    setCreationMode(null);
  };

  const handleClose = () => {
    setProjectName('');
    setTheme('');
    setLyrics('');
    setStyle('cartoon');
    setImageFormat('16:9');
    setCreationMode(null);
    onClose();
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '20px',
          padding: '16px'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
          {creationMode === null ? '🚀 Démarrer un nouveau projet' : 
           creationMode === 'theme' ? '🎵 Créer depuis un thème' : 
           '📝 Créer depuis des paroles'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {creationMode === null ? (
          // Mode selection screen
          <>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Nom du projet"
                placeholder="Ex: Mon Clip Romantique, Projet Électro..."
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                variant="outlined"
                size="large"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: '1rem'
                  },
                  mb: 2
                }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={{ fontSize: '1rem' }}>Style visuel</InputLabel>
                <Select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  label="Style visuel"
                  sx={{
                    borderRadius: '12px',
                    fontSize: '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3498DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                  }}
                >
                  <MenuItem value="cartoon">🎨 Cartoon</MenuItem>
                  <MenuItem value="realistic">📸 Réaliste</MenuItem>
                  <MenuItem value="semi-realistic">🎭 Semi-réaliste</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1rem' }}>Format d'image</InputLabel>
                <Select
                  value={imageFormat}
                  onChange={(e) => setImageFormat(e.target.value)}
                  label="Format d'image"
                  sx={{
                    borderRadius: '12px',
                    fontSize: '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3498DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                  }}
                >
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="1:1">1:1</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                  <MenuItem value="4:3">4:3</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#7F8C8D' }}>
              Choisissez comment vous souhaitez commencer votre projet musical :
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleModeSelection('theme')}
                  disabled={!projectName.trim()}
                  sx={{
                    background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                    borderRadius: '16px',
                    py: 3,
                    px: 2,
                    flexDirection: 'column',
                    gap: 1,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(52, 152, 219, 0.3)'
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                      transform: 'none',
                      boxShadow: 'none'
                    }
                  }}
                >
                  <MusicNote sx={{ fontSize: '2.5rem' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Depuis un thème
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Étape 1
                    </Typography>
                  </Box>
                </Button>
              </Grid>

              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleModeSelection('lyrics')}
                  disabled={!projectName.trim()}
                  sx={{
                    background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                    borderRadius: '16px',
                    py: 3,
                    px: 2,
                    flexDirection: 'column',
                    gap: 1,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(39, 174, 96, 0.3)'
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                      transform: 'none',
                      boxShadow: 'none'
                    }
                  }}
                >
                  <Edit sx={{ fontSize: '2.5rem' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Depuis des paroles
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Étape 2
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            </Grid>
          </>
        ) : creationMode === 'theme' ? (
          // Theme creation form
          <>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Nom du projet"
                placeholder="Ex: Mon Clip Romantique, Projet Électro..."
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                variant="outlined"
                size="large"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: '1rem'
                  },
                  mb: 2
                }}
              />
              <TextField
                fullWidth
                label="Thème"
                placeholder="Ex: Romance, Aventure, Mystère..."
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                variant="outlined"
                size="large"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: '1rem'
                  },
                  mb: 2
                }}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1rem' }}>Style visuel</InputLabel>
                <Select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  label="Style visuel"
                  sx={{
                    borderRadius: '12px',
                    fontSize: '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3498DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                  }}
                >
                  <MenuItem value="cartoon">🎨 Cartoon</MenuItem>
                  <MenuItem value="realistic">📸 Réaliste</MenuItem>
                  <MenuItem value="semi-realistic">🎭 Semi-réaliste</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1rem' }}>Format d'image</InputLabel>
                <Select
                  value={imageFormat}
                  onChange={(e) => setImageFormat(e.target.value)}
                  label="Format d'image"
                  sx={{
                    borderRadius: '12px',
                    fontSize: '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3498DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                  }}
                >
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="1:1">1:1</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                  <MenuItem value="4:3">4:3</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBackToModeSelection}
                sx={{
                  borderRadius: '25px',
                  px: 3,
                  py: 1,
                  borderColor: '#95A5A6',
                  color: '#7F8C8D',
                  '&:hover': {
                    borderColor: '#7F8C8D',
                    backgroundColor: '#F8F9FA'
                  }
                }}
              >
                Retour
              </Button>
              <Button
                variant="contained"
                onClick={handleStartFromTheme}
                disabled={!projectName.trim()}
                sx={{
                  background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                  borderRadius: '25px',
                  px: 4,
                  py: 1,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(52, 152, 219, 0.4)'
                  },
                  '&:disabled': {
                    background: '#cbd5e0',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                Créer le projet
              </Button>
            </Box>
          </>
        ) : (
          // Lyrics creation form
          <>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Nom du projet"
                placeholder="Ex: Mon Clip Romantique, Projet Électro..."
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                variant="outlined"
                size="large"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: '1rem'
                  },
                  mb: 2
                }}
              />
              <TextField
                fullWidth
                label="Thème"
                placeholder="Ex: Romance, Aventure, Mystère..."
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                variant="outlined"
                size="large"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: '1rem'
                  },
                  mb: 2
                }}
              />
              <TextField
                fullWidth
                label="Paroles de la chanson"
                placeholder="Entrez les paroles de votre chanson ici..."
                value={lyrics}
                onChange={(e) => setLyrics(e.target.value)}
                variant="outlined"
                multiline
                rows={8}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontSize: '1rem'
                  },
                  mb: 2
                }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={{ fontSize: '1rem' }}>Style visuel</InputLabel>
                <Select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  label="Style visuel"
                  sx={{
                    borderRadius: '12px',
                    fontSize: '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3498DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                  }}
                >
                  <MenuItem value="cartoon">🎨 Cartoon</MenuItem>
                  <MenuItem value="realistic">📸 Réaliste</MenuItem>
                  <MenuItem value="semi-realistic">🎭 Semi-réaliste</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1rem' }}>Format d'image</InputLabel>
                <Select
                  value={imageFormat}
                  onChange={(e) => setImageFormat(e.target.value)}
                  label="Format d'image"
                  sx={{
                    borderRadius: '12px',
                    fontSize: '1rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3498DB',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2980B9',
                    },
                  }}
                >
                  <MenuItem value="16:9">16:9</MenuItem>
                  <MenuItem value="1:1">1:1</MenuItem>
                  <MenuItem value="9:16">9:16</MenuItem>
                  <MenuItem value="4:3">4:3</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBackToModeSelection}
                sx={{
                  borderRadius: '25px',
                  px: 3,
                  py: 1,
                  borderColor: '#95A5A6',
                  color: '#7F8C8D',
                  '&:hover': {
                    borderColor: '#7F8C8D',
                    backgroundColor: '#F8F9FA'
                  }
                }}
              >
                Retour
              </Button>
              <Button
                variant="contained"
                onClick={handleStartFromLyric}
                disabled={!projectName.trim() || !theme.trim() || !lyrics.trim()}
                sx={{
                  background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                  borderRadius: '25px',
                  px: 4,
                  py: 1,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)'
                  },
                  '&:disabled': {
                    background: '#cbd5e0',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                Créer le projet
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<Close />}
          sx={{
            borderRadius: '25px',
            px: 3,
            py: 1,
            borderColor: '#95A5A6',
            color: '#7F8C8D',
            '&:hover': {
              borderColor: '#7F8C8D',
              backgroundColor: '#F8F9FA'
            }
          }}
        >
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StartNewModal;
